import React from 'react';
import colors from '../../../assets/styles/colors';
import commons from '../../../assets/styles/commons';
import { PaddingsMargins } from '../../../assets/styles/interface';

interface TypographyProps extends PaddingsMargins {
  /**
    @default 16
    @xs 12
    @sm 14
    @md 18
    @lg 20
    @xl 24
    @2xl 28
    @3xl 30
    @4xl 32
    @5xl 42*/
  fontSize?:
    | /** 12 */ 'xs'
    | /** 13*/ 'xxs'
    | /** 14*/ 'sm'
    | /** 18*/ 'md'
    | /** 20*/ 'lg'
    | /** 24*/ 'xl'
    | /** 28*/ '2xl'
    | /** 30*/ '3xl'
    | /** 32*/ '4xl'
    | /** 42*/ '5xl'
    | 'default';
  isTruncated?: boolean;
  bold?: boolean;
  underline?: boolean;
  strikeThrough?: boolean;
  italic?: boolean;
  highlight?: boolean;
  maxW?: string;
  styleOption?: 'title' | 'subtitle' | 'caption' | 'link';

  textAlign?: 'right' | 'left' | 'auto' | 'center' | 'justify';
  color?:
    | 'primary'
    | 'secondary'
    | 'textPrimary'
    | 'textSecondary'
    | 'error'
    | 'gray'
    | 'gray-light'
    | 'green'
    | 'warning'
    | 'white';

  fontWeight?:
    | 'normal'
    | '100'
    | '200'
    | '300'
    | '400'
    | '500'
    | '600'
    | '700'
    | '800'
    | '900'
    | 'bold';
  children: React.ReactNode;
  onPress?: () => void;
  alignSelf?: 'auto' | 'stretch';
  shadowStyle?: boolean;
  shadowColor?: string;
  lineHeight?: number;
  letterSpacing?: number;
  img?: string;
  style?: React.CSSProperties;
}

function Typography({
  children,
  styleOption,
  textAlign = 'left',
  alignSelf,
  shadowStyle = false,
  shadowColor = _getColor('gray'),
  lineHeight,
  letterSpacing,
  img,
  style,
  ...props
}: TypographyProps) {
  const color = _getColor(props.color || '');
  const fontSize = _getFontSize(props.fontSize || '');

  let textShadow;
  let textStyle: any;

  switch (styleOption) {
    case 'title':
      return (
        <h1
          style={{
            color: color,
            fontSize: '2em',
          }}
          {...props}
        >
          {children}
        </h1>
      );
    case 'subtitle':
      return (
        <h2
          style={{
            color: color,
            fontSize: '1.5em',
          }}
          {...props}
        >
          {children}
        </h2>
      );
    case 'caption':
      return (
        <p
          style={{
            color: color,
            fontSize: '0.75em',
          }}
          {...props}
        >
          {children}
        </p>
      );
    case 'link':
      return (
        <a
          href="#"
          style={{
            color: color,
            textDecoration: 'underline',
            fontSize: '1em',
          }}
          {...props}
        >
          {children}
        </a>
      );
    default:
      textShadow = {
        textShadow: `0 2px 5px ${shadowColor}`,
      };
      textStyle = {
        fontWeight: props.fontWeight || 'normal',
        textAlign: textAlign || 'left',
        paddingTop: (props.pt || 0) * 4,
        paddingBottom: (props.pb || 0) * 4,
        paddingLeft: (props.pl || 0) * 4,
        paddingRight: (props.pr || 0) * 4,
        marginTop: (props.mt || 0) * 4,
        marginBottom: (props.mb || 0) * 4,
        marginLeft: (props.ml || 0) * 4,
        marginRight: (props.mr || 0) * 4,
        color: color,
        fontStyle: props.italic ? 'italic' : 'normal',
        backgroundColor: props.highlight
          ? colors.neutral.paleGray
          : 'transparent',
        maxWidth: props.maxW || '100%',
        fontSize: fontSize,
        fontFamily: commons.fontFamily,
        lineHeight: lineHeight && `${lineHeight}px`,
        letterSpacing: letterSpacing ? `${letterSpacing}px` : '1px',
        alignSelf: alignSelf || 'auto',
        fontVariant: ['lining-nums'],
        ...(shadowStyle && textShadow),
        ...style,
      };
      return (
        <div style={textStyle} {...props}>
          {children}
        </div>
      );
  }
}

function _getColor(color: string) {
  switch (color) {
    case 'primary':
      return colors.primary.orange;
    case 'secondary':
      return colors.secondary.deepNavy;
    case 'success':
      return colors.success.deepGreen;
    case 'error':
      return colors.error.deepRed;
    case 'warning':
      return colors.warning.lightGold;
    case 'gray':
      return colors.neutral.gray;
    case 'white':
      return colors.secondary.white;
    default:
      return colors.neutral.black;
  }
}

function _getFontSize(fontSize: string) {
  switch (fontSize) {
    case 'xxs':
      return commons.fontSize.xxSmall;
    case 'xs':
      return commons.fontSize.xSmall;
    case 'sm':
      return commons.fontSize.small;
    case 'md':
      return commons.fontSize.medium;
    case 'lg':
      return commons.fontSize.large;
    case 'xl':
      return commons.fontSize.xLarge;
    case '2xl':
      return commons.fontSize.xxLarge;
    case '3xl':
      return commons.fontSize.xxxLarge;
    default:
      return commons.fontSize.default;
  }
}

export default Typography;
